/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/prefer-ts-expect-error */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { createRef, useEffect, useMemo } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FIELD_QUANTITY, REQUIRED_TEXT_ERROR } from '@components/dynamic-input-fields/src/utils';
import { ErrorMessage } from '@components/error/src/error-message';
import { Input } from '@components/input';
import { InputAccessValidityDates } from '@components/input-access-validity-dates';
import { InputFormFields } from '@components/input-form-fields';
import {
  CHANNELS_SALES_QUOTA_ALL,
  CHANNELS_SALES_QUOTA_CHANNEL,
  CHANNELS_SALES_QUOTA_UNLIMITED,
  SelectedChannel,
} from '@components/input-select-channels/src/input-select-channels.type';
import { InputWithTitleAndSubtitle } from '@components/input-with-title-and-subtitle';
import {
  INPUT_CHECKBOX,
  INPUT_LEFT,
} from '@components/input-with-title-and-subtitle/src/input-with-title-and-subtitle.type';
import { LabelPrimary } from '@components/label';
import { LabelInfo } from '@components/label/src/label-info';
import { MultiselectSearch } from '@components/multiselect-search/src/multiselect-search';
import { Select, SelectOption } from '@components/select';
import { Toggle } from '@components/toggle';
import { VatRateInput } from '@components/vat-rate-input';
import { DEFAULT_MAX_ITEMS_PER_TRANSACTION } from '@constants/admissions';
import { DRINKS_INCLUDED_CLUB_IDS } from '@constants/drinksincluded-club-ids';
import {
  ADMISSION_TYPE_BOTTLE_SERVICE,
  ADMISSION_TYPE_GUEST_LIST,
  ADMISSION_TYPE_TICKET,
  BOOKING_FORM_FIELDS,
  TICKET_HOLDER_FORM_FIELDS,
} from '@domain/admission';
import { Divider } from '@tremor/react';
import { calculateDefaultMaxCapacity } from '@utils/calculate-dafult-max-capacity/calculate-default-max-capacity';
import { calculateMaxItemsPerTransation } from '@utils/calculate-max-items-per-transation/calculate-max-items-per-transation';
import { isEventInThePast } from '@utils/is-event-in-the-past';
import { fromBackend } from '@utils/map-date/from-backend';
import { cannotBeNegative } from '@utils/validations/cannot-be-negative';
import { max } from '@utils/validations/max';
import { maxDecimals } from '@utils/validations/max-decimals';
import { min } from '@utils/validations/min';
import classNames from 'classnames';

import { Form } from './admission.type';
import { AdvancedFormProps } from './advanced.form.types';

export const AdvancedForm = ({
  offer,
  admissionChannels,
  setAdmissionsChannels,
  type,
  clubState,
  event,
  maxEndDate,
  isDDPInputDisabled,
  isDuplicatedAdmission,
  setValidityglobalError,
  validityglobalError,
}: AdvancedFormProps) => {
  const { t } = useTranslation();
  const methods = useFormContext<Form>();
  const { register, unregister, control, watch, formState, setValue, getValues, setError, clearErrors, trigger } =
    methods;
  const { errors } = formState;
  // @ts-ignore
  const quantity = watch(FIELD_QUANTITY);
  const isPrivate = watch('isPrivate');
  const isForAllChannels = watch('isForAllChannels');
  const isNominative = watch('isNominative');
  const salesQuota = watch('salesQuota');

  const ref = createRef<HTMLDivElement>();

  const isPastEvent = isEventInThePast(event);

  useEffect(() => {
    if ((isPrivate && offer?.settings.isPrivate === false) || (isPrivate && !offer))
      setValue('isForAllChannels', true, { shouldDirty: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivate]);

  useEffect(() => {
    if (salesQuota !== CHANNELS_SALES_QUOTA_ALL) unregister('limitAllChannels');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesQuota]);

  // @ts-ignore
  const { fields: ticketHolderFormFields, append: appendHolderFormField } = useFieldArray({
    control,
    name: TICKET_HOLDER_FORM_FIELDS,
  });

  const { fields: bookingFormFields, append: appendBookingFormField } = useFieldArray({
    control,
    name: BOOKING_FORM_FIELDS,
  });

  const handleNewTicketHolderFormField = (label: string) => {
    appendHolderFormField({
      name: label,
      dataType: 'text',
      isRequired: true,
      disabled: false,
    });
  };

  const handleNewTicketBookingFormField = (label: string, maxNum?: number, minNum?: number, dataType?: string) => {
    if (!dataType) dataType = 'text';
    appendBookingFormField({
      name: label,
      dataType,
      isRequired: true,
      disabled: false,
      max: maxNum,
      min: minNum,
    });
  };

  const handleSelectAllChannels = (value: boolean) => {
    const allChannels = value
      ? admissionChannels.map((channel) => ({
          ...channel,
          checked: true,
        }))
      : admissionChannels.map((channel) => ({
          ...channel,
          checked: false,
        }));
    setAdmissionsChannels(allChannels);
    setValue('isChannelsDirty', true, { shouldDirty: true });

    if (!value)
      setError('salesQuota', {
        type: 'custom',
        message: 'no channel selected',
      });
    if (value) clearErrors();
  };

  const handleSalesChannelsChange = (id: string, value: boolean) => {
    const allChannels = admissionChannels.map((channel) => {
      if (channel.id === id) {
        return {
          ...channel,
          checked: value,
        };
      }
      return channel;
    });

    setAdmissionsChannels(allChannels);
    setValue('isChannelsDirty', true, { shouldDirty: true });

    const areSelectedChannels = allChannels.filter((channel) => !!channel.checked);

    if (areSelectedChannels.length === 0)
      setError('salesQuota', {
        type: 'custom',
        message: 'no channel selected',
      });
    else clearErrors();
  };

  const multiSearchTranslations = useMemo(() => {
    return {
      deselectAll: t('offers:deselectAllLabel'),
      selectAll: t('offers:selectAllLabel'),
      noResultsFound: t('offers:admissionTicketSalesChannelsNoResultsFound'),
      noOptionsSelected: t('offers:admissionTicketSalesChannelsNoneSelected'),
      searchLabel: t('offers:admissionTicketSalesChannelsSearchLabel'),
      searchPlaceholder: t('offers:admissionTicketSalesChannelsSearchPlaceholder'),
    };
  }, [t]);

  const hasOfferSoldItems = useMemo(() => {
    return offer ? offer.soldItems > 0 : false;
  }, [offer]);

  const selectedChannels: SelectedChannel[] = useMemo(() => {
    return admissionChannels
      .map((channel, originalIndex) => ({ ...channel, originalIndex }))
      .filter((channel) => !!channel.checked);
  }, [admissionChannels]);

  const isClubCountryItaly = clubState.city.country.isoCode === 'ITA';
  const isTicketOrBottleService = type === ADMISSION_TYPE_BOTTLE_SERVICE || type === ADMISSION_TYPE_TICKET;
  const isDdpActive = isClubCountryItaly && isTicketOrBottleService;
  const isDrinksIncludedActive = DRINKS_INCLUDED_CLUB_IDS.includes(clubState.id);

  const extraFieldsActives = isDdpActive || isDrinksIncludedActive;

  const minValidFromDate = event?.startingTime ? fromBackend(event.startingTime) : undefined;

  const watchValidFromDate = watch('validFrom');
  const minValidUntilDate = useMemo(() => {
    if (watchValidFromDate) {
      return watchValidFromDate;
    }
    return minValidFromDate;
  }, [watchValidFromDate, minValidFromDate]);

  const defaultMaxCapacity = calculateDefaultMaxCapacity(type);

  const minItemsPerTransationRegister = register('itemsPerTransaction.min', {
    validate: {
      cannotBeNegative: (value) =>
        cannotBeNegative({
          value,
          message: t('offers:admissionMinItemsPerTransactionNegativeError'),
        }),
      maxDecimals: (value) =>
        maxDecimals({
          value,
          decimals: 0,
          message: t('offers:admissionMinItemsPerTransactionDecimalsError'),
        }),
      max: () => {
        const minItems = getValues('itemsPerTransaction.min');
        const maxItemsField = getValues('itemsPerTransaction.max');
        const maxValue = maxItemsField === '' ? defaultMaxCapacity : maxItemsField;
        return max({
          value: minItems,
          maxValue,
          message: t('offers:admissionMinItemsPerTransactionMaxError', {
            maxValue,
          }),
        });
      },
    },
  });

  const validateMinItemPerTranslation = () => {
    const minItems = getValues('itemsPerTransaction.min');
    const maxItemsField = getValues('itemsPerTransaction.max');
    const maxValue = maxItemsField === '' ? defaultMaxCapacity : maxItemsField;
    clearErrors('itemsPerTransaction.min');
    if (Number(minItems) > Number(maxValue))
      setError('itemsPerTransaction.min', {
        type: 'min',
        message: t('offers:admissionMinItemsPerTransactionMaxError', {
          maxValue,
        }),
      });
  };

  const maxItemsPerTransationRegister = register('itemsPerTransaction.max', {
    validate: {
      cannotBeNegative: (value) =>
        cannotBeNegative({
          value,
          message: t('offers:admissionMaxItemsPerTransactionNegativeError'),
        }),
      maxDecimals: (value) =>
        maxDecimals({
          value,
          decimals: 0,
          message: t('offers:admissionMaxItemsPerTransactionDecimalsError'),
        }),
      max: () => {
        const quantityPurchase = quantity ? Number(quantity) : null;
        const maxItems = getValues('itemsPerTransaction.max');
        const maxValue = calculateMaxItemsPerTransation(quantityPurchase);
        return max({
          value: maxItems,
          maxValue,
          message: t('offers:admissionMaxItemsPerTransactionMaxError', {
            maxValue,
          }),
        });
      },
      min: () => {
        const maxItems = getValues('itemsPerTransaction.max');
        const maxValue = maxItems === '' ? defaultMaxCapacity : maxItems;
        const minValue = getValues('itemsPerTransaction.min');
        return min({
          value: maxValue,
          minValue,
          message: t('offers:admissionMaxItemsPerTransactionMinError', {
            minValue,
          }),
        });
      },
    },
  });

  const validateMaxItemsPerTransaction = () => {
    const quantityPurchase = quantity ? Number(quantity) : null;
    const maxItems = getValues('itemsPerTransaction.max');
    const maxValue = calculateMaxItemsPerTransation(quantityPurchase);
    clearErrors('itemsPerTransaction.max');
    if (Number(maxItems) > maxValue)
      setError('itemsPerTransaction.max', {
        type: 'max',
        message: t('offers:admissionMaxItemsPerTransactionMinError', {
          minValue: maxValue,
        }),
      });
  };

  const excludeSiaeTranslations = useMemo(() => {
    return {
      title: t('offers:excludeSiaeCheckboxTitle'),
      subtitle: t('offers:excludeSiaeCheckboxSubtitle'),
    };
  }, [t]);

  const isOfferSiaeExcluded = offer?.settings.italyRegulation?.isSiaeExcluded;

  const isExcludedSiaeCheckboxDisabled = event?.settings.isPublished && isOfferSiaeExcluded;

  const showExcludedSiaeCheckbox =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    !!event?.companyClub.company?.isAllowedToExcludeSiaeOffers &&
    type !== ADMISSION_TYPE_GUEST_LIST &&
    (!offer || isExcludedSiaeCheckboxDisabled || isDuplicatedAdmission || !event?.settings.isPublished);

  return (
    <div id='advanced-form' className='overflow-hidden transition-all duration-700 ease-in-out'>
      <div ref={ref}>
        <Divider className='my-12' />
        {event && (
          <div className='grid grid-cols-2 md:grid-cols-3 gap-6 items-start mb-8'>
            <div>
              <LabelPrimary
                className='text-left '
                htmlFor='itemsPerTransaction.min'
                hasError={!!errors.itemsPerTransaction?.min}>
                {t('offers:admissionMinItemsPerTransactionLabel')}
              </LabelPrimary>

              <Input
                id='itemsPerTransaction.min'
                type='number'
                step={1}
                min='1'
                max={DEFAULT_MAX_ITEMS_PER_TRANSACTION}
                placeholder={'1'}
                disabled={isPastEvent}
                readOnly={isPastEvent}
                hasError={!!errors.itemsPerTransaction?.min}
                {...minItemsPerTransationRegister}
                onChange={(e) => {
                  minItemsPerTransationRegister.onChange(e);
                  validateMaxItemsPerTransaction();
                }}
              />
              <ErrorMessage name='itemsPerTransaction.min' />
            </div>
            <div>
              <LabelPrimary
                className='text-left'
                htmlFor='itemsPerTransaction.max'
                hasError={!!errors.itemsPerTransaction?.max}>
                {t('offers:admissionMaxItemsPerTransactionLabel')}
              </LabelPrimary>

              <Input
                id='itemsPerTransaction.max'
                type='number'
                step={1}
                min='1'
                max={DEFAULT_MAX_ITEMS_PER_TRANSACTION}
                disabled={isPastEvent}
                readOnly={isPastEvent}
                placeholder={defaultMaxCapacity}
                hasError={!!errors.itemsPerTransaction?.max}
                {...maxItemsPerTransationRegister}
                onChange={(e) => {
                  maxItemsPerTransationRegister.onChange(e);
                  validateMinItemPerTranslation();
                }}
              />
              <ErrorMessage name='itemsPerTransaction.max' />
            </div>

            {extraFieldsActives && isDdpActive && (
              <div>
                <LabelPrimary htmlFor='ddp' hasError={!!errors.ddp}>
                  {t('offers:admissionDDPLabel')}
                </LabelPrimary>
                <Input
                  id='ddp'
                  type='number'
                  placeholder={t('offers:admissionPricePlaceholder')}
                  min='0'
                  step='.01'
                  readOnly={hasOfferSoldItems || isPastEvent || isDDPInputDisabled}
                  disabled={isPastEvent || isDDPInputDisabled}
                  hasError={!!errors.ddp}
                  {...register('ddp', {
                    validate: {
                      cannotBeNegative: (value) =>
                        cannotBeNegative({
                          value,
                          message: t('offers:admissionDDPCannotBeNegativeError'),
                        }),
                      maxDecimals: (value) =>
                        maxDecimals({
                          value,
                          message: t('offers:admissionDDPDecimalsError'),
                          decimals: 2,
                        }),
                    },
                  })}
                />
                <ErrorMessage name='ddp' />
              </div>
            )}
            {showExcludedSiaeCheckbox && (
              <div className='col-span-2 md:col-span-3'>
                <InputWithTitleAndSubtitle
                  translations={excludeSiaeTranslations}
                  controllerName='isSiaeExcluded'
                  inputType={INPUT_CHECKBOX}
                  inputPosition={INPUT_LEFT}
                  textWidthClassname='w-max'
                  disabled={isExcludedSiaeCheckboxDisabled}
                  checked={isOfferSiaeExcluded}
                />
              </div>
            )}
            {extraFieldsActives && isDrinksIncludedActive && (
              <div>
                <LabelPrimary htmlFor='drinksIncluded' hasError={!!errors.drinksIncluded}>
                  {t('offers:admissionDrinksIncludedLabel')}
                </LabelPrimary>
                <Input
                  id='drinksIncluded'
                  type='number'
                  placeholder={t('offers:admissionDrinksIncludedPlaceholder')}
                  min='0'
                  step='1'
                  hasError={!!errors.drinksIncluded}
                  disabled={isPastEvent}
                  readOnly={isPastEvent}
                  {...register('drinksIncluded', {
                    validate: {
                      cannotBeNegative: (value) =>
                        cannotBeNegative({
                          value,
                          message: t('offers:admissionDrinksIncludedCannotBeNegativeError'),
                        }),
                      maxDecimals: (value) =>
                        maxDecimals({
                          value,
                          message: t('offers:admissionDrinksIncludedDecimalsError'),
                          decimals: 0,
                        }),
                    },
                  })}
                />
                <ErrorMessage name='drinksIncluded' />
              </div>
            )}
          </div>
        )}

        {event && (
          <div className='my-12'>
            <InputAccessValidityDates
              timezone={clubState.city.timezone}
              isEventInThePast={isPastEvent}
              minValidFromDate={minValidFromDate}
              minValidUntilDate={minValidUntilDate}
              maxEndDate={maxEndDate}
              validityglobalError={validityglobalError}
              setValidityglobalError={setValidityglobalError}
            />
          </div>
        )}

        <div className='flex flex-wrap justify-between md:flex-row gap-4'>
          <div>
            <LabelInfo text={t('offers:admissionTicketIsForAllChannelsTooltip')}>
              {t('offers:admissionTicketIsForAllChannelsLabel')}
            </LabelInfo>

            <Controller
              name='isForAllChannels'
              control={control}
              render={({ field }) => (
                <Toggle
                  isEnabledText={t('offers:admissionTicketIsForAllChannelsOpen')}
                  isNotEnabledText={t('offers:admissionTicketIsForAllChannelsLimited')}
                  {...field}
                  disabled={isPrivate || isPastEvent}
                />
              )}
            />
          </div>

          <div>
            <LabelInfo htmlFor='isPrivate' text={t('offers:admissionTicketIsPrivateTooltip')}>
              {t('offers:admissionTicketIsPrivateLabel')}
            </LabelInfo>

            <Controller
              name='isPrivate'
              control={control}
              render={({ field }) => (
                <Toggle
                  isEnabledText={t('offers:admissionTicketIsPrivatePublic')}
                  isNotEnabledText={t('offers:admissionTicketIsPrivateImportOnly')}
                  {...field}
                  disabled={isPastEvent}
                />
              )}
            />
          </div>
        </div>

        <div
          className={classNames('mt-8', {
            block: isForAllChannels,
            hidden: !isForAllChannels,
          })}>
          <LabelPrimary htmlFor='accessCode' hasError={false}>
            {t('offers:admissionTicketAccessCodeLabel')}
          </LabelPrimary>

          <Input
            id='accessCode'
            placeholder={t('offers:admissionTicketAccessCodePlaceholder')}
            hasError={!!errors.accessCode}
            disabled={isPastEvent}
            readOnly={isPastEvent}
            {...register('accessCode', {
              validate: {
                isRegExpValid: (value) => {
                  return /^\w*$/.test(value ?? '');
                },
              },
            })}
          />

          <ErrorMessage name='accessCode' />
        </div>

        <div
          className={classNames('mt-8', {
            block: isForAllChannels,
            hidden: !isForAllChannels,
          })}>
          <LabelInfo htmlFor='isPrivate' text={t('offers:admissionTicketSalesChannelTooltip')}>
            {t('offers:admissionTicketSalesChannelLabel')}
          </LabelInfo>
          <div>
            <MultiselectSearch
              translations={multiSearchTranslations}
              options={admissionChannels}
              disabled={isPastEvent}
              readOnly={isPastEvent}
              handleSelectedAll={handleSelectAllChannels}
              onChange={handleSalesChannelsChange}
            />
          </div>

          {!!selectedChannels.length && (
            <div className='mt-5 ml-4'>
              <LabelPrimary htmlFor='salesQuota'>{t('offers:admissionTicketSalesQuotaLabel')}</LabelPrimary>

              <Select
                {...register('salesQuota', {
                  required: t(REQUIRED_TEXT_ERROR),
                  onChange: () => setValue('isChannelsDirty', true, { shouldDirty: true }),
                })}
                disabled={isPastEvent}
                readOnly={isPastEvent}
                hasError={!!errors.salesQuota}>
                <SelectOption
                  value={CHANNELS_SALES_QUOTA_UNLIMITED}
                  label={t('offers:admissionTicketSalesQuotaUnlimitedOption')}
                />
                <SelectOption
                  value={CHANNELS_SALES_QUOTA_CHANNEL}
                  label={t('offers:admissionTicketSalesQuotaLimitByChannelOption')}
                />
                <SelectOption
                  value={CHANNELS_SALES_QUOTA_ALL}
                  label={t('offers:admissionTicketSalesQuotaLimitChannelAllOption')}
                />
              </Select>

              <ErrorMessage name='salesQuota' />
            </div>
          )}

          {salesQuota === CHANNELS_SALES_QUOTA_CHANNEL && (
            <div className='mt-6 ml-8 flex flex-col gap-y-4'>
              {selectedChannels.map((channel, index) => {
                return (
                  <div key={channel.id}>
                    <div className='flex flex-row items-center justify-items-start'>
                      <div className='w-20'>
                        <Input
                          id={`channels.${channel.originalIndex}`}
                          name={`channels.${channel.originalIndex}`}
                          type='number'
                          placeholder=' - '
                          step={1}
                          label={channel.name}
                          hasError={errors.channels ? !!errors.channels[index] : false}
                          disabled={isPastEvent}
                          readOnly={isPastEvent}
                          value={channel.value}
                          onChange={(value) => {
                            const val = value.currentTarget.value;

                            setAdmissionsChannels(
                              admissionChannels.map((admissionChannel, admissionIndex) => {
                                if (admissionIndex === channel.originalIndex)
                                  return {
                                    ...admissionChannel,
                                    value: val,
                                  };

                                return {
                                  ...admissionChannel,
                                };
                              })
                            );
                            setValue('isChannelsDirty', true, { shouldDirty: true });

                            const isMin = min({
                              value: val,
                              allowEmptyValue: true,
                              minValue: 1,
                              message: t('offers:admissionSalesChannelsQuotaErrorMin', {
                                number: 1,
                              }),
                            });

                            const isMax = max({
                              value: val,
                              maxValue: quantity,
                              message: t('offers:admissionSalesChannelsQuotaErrorMax', {
                                quantity,
                              }),
                            });
                            const isDecimals = maxDecimals({
                              value: val,
                              message: t('offers:admissionSalesChannelsQuotaErrorDecimals'),
                              decimals: 0,
                            });

                            if (isMin)
                              setError(`channels.${index}.value`, {
                                type: 'custom',
                                message: isMin,
                              });
                            else if (isMax)
                              setError(`channels.${index}.value`, {
                                type: 'custom',
                                message: isMax,
                              });
                            else if (isDecimals)
                              setError(`channels.${index}.value`, {
                                type: 'custom',
                                message: isDecimals,
                              });
                            else {
                              clearErrors(`channels.${index}.value`);
                              trigger(`channels.${index}.value`);
                            }
                          }}
                        />
                      </div>

                      <label className='ml-3 text-primary text-body-s' htmlFor={`channels.${index}`}>
                        {channel.name}
                      </label>
                    </div>
                    <ErrorMessage name={`channels.${index}.value`} />
                  </div>
                );
              })}
            </div>
          )}
          {salesQuota === CHANNELS_SALES_QUOTA_ALL && (
            <>
              <div className='mt-6 ml-8'>
                <LabelPrimary htmlFor='salesQuota'>
                  {t('offers:admissionTicketSalesQuotaLimitByChannelLabel')}
                </LabelPrimary>

                <div className='w-48'>
                  <Input
                    id='limitAllChannels'
                    type='number'
                    step={1}
                    min='1'
                    hasError={!!errors.limitAllChannels}
                    disabled={isPastEvent}
                    readOnly={isPastEvent}
                    {...register('limitAllChannels', {
                      // eslint-disable-next-line
                      required: t('offers:admissionSalesChannelsQuotaErrorRequired'),
                      validate: {
                        cannotBeNegative: (value) => {
                          // eslint-disable-next-line
                          // @ts-ignore
                          const val = value as string;
                          return cannotBeNegative({
                            value: val,
                            message: t('offers:admissionSalesChannelsQuotaErrorNegative'),
                          });
                        },
                        min: (value) =>
                          min({
                            value,
                            minValue: 1,
                            message: t('offers:admissionSalesChannelsQuotaErrorMin', {
                              number: 1,
                            }),
                          }),
                        max: (value) =>
                          max({
                            value,
                            maxValue: quantity,
                            message: t('offers:admissionSalesChannelsQuotaErrorMax', {
                              quantity,
                            }),
                          }),
                        maxDecimals: (value) => {
                          // eslint-disable-next-line
                          // @ts-ignore
                          const val = value as string;
                          return maxDecimals({
                            value: val,
                            message: t('offers:admissionSalesChannelsQuotaErrorDecimals'),
                            decimals: 0,
                          });
                        },
                      },
                    })}
                  />
                </div>
              </div>
              <ErrorMessage name='limitAllChannels' />
            </>
          )}
        </div>

        <hr className='my-12 border-t border-gray-300 dark:border-gray-900' />

        <div>
          <LabelInfo htmlFor='isNominative' text={t('offers:admissionTicketIsNominativeTooltip')}>
            {t('offers:admissionTicketIsNominativeLabel')}
          </LabelInfo>
          <Controller
            name='isNominative'
            control={control}
            render={({ field }) => {
              return (
                <Toggle
                  isEnabledText={t('offers:admissionTicketIsNominativeTransferable')}
                  isNotEnabledText={t('offers:admissionTicketIsNominativeNominative')}
                  {...field}
                  disabled={hasOfferSoldItems || isPastEvent}
                />
              );
            }}
          />
        </div>

        <div className='mt-8'>
          <InputFormFields
            showModal
            formFieldName={BOOKING_FORM_FIELDS}
            label={t('offers:admissionTicketBookingFormFieldsLabel')}
            fields={bookingFormFields}
            disableConditions={hasOfferSoldItems || isPastEvent}
            appendFields={handleNewTicketBookingFormField}
          />
        </div>

        {isNominative && (
          <div className='mt-8'>
            <InputFormFields
              formFieldName={TICKET_HOLDER_FORM_FIELDS}
              label={t('offers:admissionTicketHolderFormFieldsLabel')}
              fields={ticketHolderFormFields}
              disableConditions={hasOfferSoldItems || isPastEvent}
              appendFields={handleNewTicketHolderFormField}
            />
          </div>
        )}

        {!isClubCountryItaly && <VatRateInput disabled={isPastEvent} readOnly={isPastEvent} />}
      </div>
    </div>
  );
};
